import { intlFormat, isSameDay } from 'date-fns';
import type { ReactNode } from 'react';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmEventDetails } from '../../../server/types/infer-trpc-types.ts';
import { CommentsThread } from '../../comments/components/comments-thread.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { useUpdateNote } from '../../notes/hooks/use-update-note.ts';
import { EventNoteCreateForm } from './event-note-create.tsx';
import { EventNoteForm } from './event-note-form.tsx';

type EventNoteDialogProps = ModalInnerProps & {
    event: CrmEventDetails;
    occurrenceDate: string;
    onSuccess?: () => void;
};

export const EventNoteDialog = DialogStateManager.create<EventNoteDialogProps>(
    ({ event, occurrenceDate, onSuccess }): ReactNode => {
        const { t, locale } = useLocale('events');
        const { visible, remove } = useDialog();

        const updateNoteMutation = useUpdateNote();

        const occurrenceDateObj = new Date(occurrenceDate);
        const noteDetails = event.notes.find((note) => isSameDay(note.occurredAt, occurrenceDateObj));

        return (
            <Dialog open={visible} showClose onClose={remove}>
                <div className="flex flex-col gap-3 p-6 sm:w-[640px] h-full sm:h-auto sm:max-h-[80vh] overflow-y-auto">
                    <Text color="neutral-950" weight="medium">
                        {t('note_for_date', {
                            date: intlFormat(
                                occurrenceDateObj,
                                { month: 'long', day: 'numeric', year: 'numeric' },
                                { locale },
                            ),
                        })}
                    </Text>
                    {noteDetails ? (
                        <>
                            <EventNoteForm
                                note={noteDetails}
                                onUpdate={(values) => {
                                    updateNoteMutation.mutate({
                                        noteId: noteDetails.noteId,
                                        title: values.title,
                                        attachments: values.attachments,
                                    });
                                }}
                            />
                            <CommentsThread noteId={noteDetails.noteId} />
                        </>
                    ) : (
                        event.customerId && (
                            <EventNoteCreateForm
                                event={{ ...event, customerId: event.customerId }}
                                occurrenceDate={occurrenceDate}
                                onCancel={remove}
                                onSuccess={() => {
                                    onSuccess?.();
                                    remove();
                                }}
                            />
                        )
                    )}
                </div>
            </Dialog>
        );
    },
);
